"use client"

import { useState, useEffect, useCallback } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  Box,
  Button,
  Container,
  FormControl,
  Grid,
  Paper,
  TextField,
  Typography,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
  Divider,
  Alert,
} from "@mui/material"
import {
  Restaurant as RestaurantIcon,
  Schedule as ScheduleIcon,
  Description as DescriptionIcon,
  Send as SendIcon,
  AccessTime as TimeIcon,
  Fastfood as FastfoodIcon,
  Store as StoreIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
} from "@mui/icons-material"
import { toast } from "react-toastify"
import apiClient from "../utils/apiClient"
import { updateRequest } from "../api"
import moment from "moment-timezone"
import { motion, AnimatePresence } from "framer-motion"
import LoadingSpinner from "../components/LoadingSpinner"
import { useSlots } from "../contexts/SlotsContext"

const API_BASE_URL = process.env.REACT_APP_API_URL
const KOLKATA_TIMEZONE = "Asia/Kolkata"

const RequestForm = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { refreshSlots } = useSlots()
  const [loading, setLoading] = useState(false)
  const [loadingSlots, setLoadingSlots] = useState(true)
  const [activeStep, setActiveStep] = useState(0)
  const [slots, setSlots] = useState([])
  const [formData, setFormData] = useState({
    reason: "",
    selectedSlotId: "",
    selectedItemOption: "",
  })

  const steps = ["Select Slot", "Request Details", "Review & Submit"]

  // Fetch available slots
  const fetchSlots = useCallback(async () => {
    setLoadingSlots(true)
    try {
      const response = await apiClient.get(`/admin/slots`)

      const slotData = response.data.slot
      if (slotData && slotData.is_active) {
        setSlots(slotData.slots || [])
      } else {
        setSlots([])
      }
    } catch (error) {
      console.error("Error fetching slots:", error)

      toast.error("Failed to fetch available slots")
      setSlots([])
    } finally {
      setLoadingSlots(false)
    }
  }, [])

  useEffect(() => {
    fetchSlots()
  }, [fetchSlots])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))

    // Reset selected item option when slot changes
    if (name === "selectedSlotId") {
      setFormData((prev) => ({
        ...prev,
        selectedItemOption: "",
      }))
    }
  }

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const selectedSlot = slots.find((slot) => slot.id === formData.selectedSlotId)

      const dataToSend = {
        reason: formData.reason,
        slotId: formData.selectedSlotId,
        period: selectedSlot?.name || "",
        mealType: formData.selectedItemOption,
        isIndividual: true,
        foodCount: 1,
      }

      if (id) {
        await updateRequest(id, dataToSend)
        toast.success("Request updated successfully!")
      } else {
        await apiClient.post(`/requests`, dataToSend)
        toast.success("Request submitted successfully!")
        refreshSlots() // Refresh slots data after placing order
      }
      navigate("/my-requests")
    } catch (error) {

      console.error("Error submitting request:", error)
      toast.error(error.response?.data?.message || "Failed to submit request")
    } finally {
      setLoading(false)
    }
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }

  const getSelectedSlot = () => {
    return slots.find((slot) => slot.id === formData.selectedSlotId) || null
  }

  const isSlotActive = (slot) => {
    const now = moment().tz(KOLKATA_TIMEZONE)
    const startTime = moment(slot.startTime, "HH:mm").tz(KOLKATA_TIMEZONE)
    const endTime = moment(slot.endTime, "HH:mm").tz(KOLKATA_TIMEZONE)

    // Check if current time is within slot time range
    return now.isBetween(startTime, endTime)
  }

  const isSlotUpcoming = (slot) => {
    const now = moment().tz(KOLKATA_TIMEZONE)
    const startTime = moment(slot.startTime, "HH:mm").tz(KOLKATA_TIMEZONE)

    // Check if slot start time is in the future
    return startTime.isAfter(now)
  }

  const isSlotPassed = (slot) => {
    const now = moment().tz(KOLKATA_TIMEZONE)
    const endTime = moment(slot.endTime, "HH:mm").tz(KOLKATA_TIMEZONE)

    // Check if slot end time is in the past
    return endTime.isBefore(now)
  }

  const getSlotStatus = (slot) => {
    if (isSlotActive(slot)) return "active"
    if (isSlotUpcoming(slot)) return "upcoming"
    if (isSlotPassed(slot)) return "passed"
    return "unknown"
  }

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
          >
            <Card
              sx={{
                background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255,255,255,0.3)",
                borderRadius: 3,
                boxShadow: "0 15px 50px rgba(0,0,0,0.08)",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
                    <ScheduleIcon
                      sx={{
                        fontSize: { xs: 20, sm: 24, md: 28 },
                        background: "linear-gradient(45deg, #20d5d5 30%, #16B1C8 90%)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    />
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{
                      fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
                      background: "linear-gradient(45deg, #20d5d5 30%, #16B1C8 90%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Select Available Slot
                  </Typography>
                </Box>

                {loadingSlots ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={40} sx={{ color: "#20d5d5" }} />
                  </Box>
                ) : slots.length === 0 ? (
                  <Alert
                    severity="info"
                    icon={<BlockIcon />}
                    sx={{
                      borderRadius: 2,
                      mb: 2,
                      background: "linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(3, 169, 244, 0.1) 100%)",
                      border: "1px solid rgba(33, 150, 243, 0.3)",
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      No slots available today
                    </Typography>
                    <Typography variant="body2">
                      There are no active food slots available for today.
                    </Typography>
                  </Alert>
                ) : (
                  <Grid container spacing={2}>
                    {slots.map((slot) => {
                      const status = getSlotStatus(slot)
                      const isActive = status === "active"
                      const isUpcoming = status === "upcoming"
                      const isPassed = status === "passed"
                      const isSelectable = isActive

                      return (
                        <Grid item xs={12} key={slot.id}>
                          <Card
                            sx={{
                              borderRadius: 2,
                              border: formData.selectedSlotId === slot.id ? "2px solid #20d5d5" : "1px solid #e0e0e0",
                              boxShadow:
                                formData.selectedSlotId === slot.id
                                  ? "0 8px 25px rgba(32, 213, 213, 0.2)"
                                  : "0 2px 10px rgba(0,0,0,0.05)",
                              opacity: !isSelectable ? 0.7 : 1,
                              transition: "all 0.3s ease",
                              position: "relative",
                              overflow: "hidden",
                              "&::before":
                                formData.selectedSlotId === slot.id
                                  ? {
                                      content: '""',
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      background:
                                        "linear-gradient(135deg, rgba(32, 213, 213, 0.1) 0%, rgba(22, 177, 200, 0.1) 100%)",
                                      pointerEvents: "none",
                                    }
                                  : {},
                            }}
                          >
                            <CardContent sx={{ p: 2 }}>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={6}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography variant="h6" fontWeight={700}>
                                      {slot.name}
                                    </Typography>
                                    {isActive && (
                                      <Chip
                                        label="Active Now"
                                        size="small"
                                        color="success"
                                        icon={<CheckCircleIcon fontSize="small" />}
                                        sx={{ fontWeight: 600 }}
                                      />
                                    )}
                                    {isUpcoming && (
                                      <Chip label="Upcoming" size="small" color="primary" sx={{ fontWeight: 600 }} />
                                    )}
                                    {isPassed && (
                                      <Chip
                                        label="Closed"
                                        size="small"
                                        color="error"
                                        icon={<BlockIcon fontSize="small" />}
                                        sx={{ fontWeight: 600 }}
                                      />
                                    )}
                                  </Box>

                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                                    <TimeIcon fontSize="small" sx={{ color: "#64748b" }} />
                                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                      {slot.startTime} - {slot.endTime}
                                    </Typography>
                                  </Box>
                                </Grid>

                                <Grid
                                  item
                                  xs={12}
                                  sm={6}
                                  sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" } }}
                                >
                                  <Button
                                    variant={formData.selectedSlotId === slot.id ? "contained" : "outlined"}
                                    disabled={!isSelectable}
                                    onClick={() => handleChange({ target: { name: "selectedSlotId", value: slot.id } })}
                                    sx={{
                                      borderRadius: 2,
                                      px: 3,
                                      py: 1,
                                      background:
                                        formData.selectedSlotId === slot.id
                                          ? "linear-gradient(135deg, #20d5d5 0%, #16B1C8 100%)"
                                          : "transparent",
                                      borderColor: "#20d5d5",
                                      color: formData.selectedSlotId === slot.id ? "white" : "#20d5d5",
                                      "&:hover": {
                                        background:
                                          formData.selectedSlotId === slot.id
                                            ? "linear-gradient(135deg, #16B1C8 0%, #0e7490 100%)"
                                            : "rgba(32, 213, 213, 0.1)",
                                        borderColor: "#16B1C8",
                                      },
                                      "&:disabled": {
                                        background: "#f5f5f5",
                                        borderColor: "#e0e0e0",
                                        color: "#bdbdbd",
                                      },
                                    }}
                                  >
                                    {formData.selectedSlotId === slot.id
                                      ? "Selected"
                                      : isActive
                                        ? "Select"
                                        : isUpcoming
                                          ? "Upcoming"
                                          : "Closed"}
                                  </Button>
                                </Grid>

                                {formData.selectedSlotId === slot.id && (
                                  <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />

                                    <Box sx={{ mt: 1 }}>
                                      {/* Restaurants */}
                                      {slot.restaurants && slot.restaurants.length > 0 && (
                                        <Box sx={{ mb: 2 }}>
                                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                            <StoreIcon fontSize="small" sx={{ color: "#64748b" }} />
                                            <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                              Available Restaurants:
                                            </Typography>
                                          </Box>
                                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                            {slot.restaurants.map((restaurant, idx) => (
                                              <Chip
                                                key={idx}
                                                label={restaurant}
                                                size="small"
                                                sx={{
                                                  background: "linear-gradient(135deg, #06d6a0 0%, #20d5d5 100%)",
                                                  color: "white",
                                                  fontWeight: 600,
                                                }}
                                              />
                                            ))}
                                          </Box>
                                        </Box>
                                      )}

                                      {/* Food Items */}
                                      {slot.foodItems && slot.foodItems.length > 0 && (
                                        <Box sx={{ mb: 2 }}>
                                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                            <FastfoodIcon fontSize="small" sx={{ color: "#64748b" }} />
                                            <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                              Available Food Items:
                                            </Typography>
                                          </Box>
                                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                            {slot.foodItems.map((item, idx) => (
                                              <Chip
                                                key={idx}
                                                label={item}
                                                size="small"
                                                sx={{
                                                  background: "linear-gradient(135deg, #ff9800 0%, #ff5722 100%)",
                                                  color: "white",
                                                  fontWeight: 600,
                                                }}
                                              />
                                            ))}
                                          </Box>
                                        </Box>
                                      )}

                                      {/* Item Options */}
                                      {slot.item_options && slot.item_options.length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                                            Select Item Option:
                                          </Typography>
                                          <FormControl fullWidth>
                                            <Select
                                              value={formData.selectedItemOption}
                                              onChange={handleChange}
                                              name="selectedItemOption"
                                              displayEmpty
                                              required
                                              sx={{
                                                borderRadius: 2,
                                                "& .MuiOutlinedInput-notchedOutline": {
                                                  borderColor: "#e0e0e0",
                                                },
                                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                                  borderColor: "#20d5d5",
                                                },
                                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                  borderColor: "#16B1C8",
                                                },
                                              }}
                                            >
                                              <MenuItem value="" disabled>
                                                <em>Select an option</em>
                                              </MenuItem>
                                              {slot.item_options.map((option) => {
                                                const isVeg = option.includes("(Veg)")
                                                const isNonVeg = option.includes("(Non-Veg)")
                                                
                                                return (
                                                  <MenuItem key={option} value={option}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                      {isVeg && (
                                                        <Box sx={{ 
                                                          width: 8, 
                                                          height: 8, 
                                                          borderRadius: "50%", 
                                                          background: "#2e7d32" 
                                                        }} />
                                                      )}
                                                      {isNonVeg && (
                                                        <Box sx={{ 
                                                          width: 8, 
                                                          height: 8, 
                                                          borderRadius: "50%", 
                                                          background: "#d32f2f" 
                                                        }} />
                                                      )}
                                                      <Typography 
                                                        sx={{ 
                                                          color: isVeg ? "#2e7d32" : isNonVeg ? "#d32f2f" : "inherit",
                                                          fontWeight: (isVeg || isNonVeg) ? 600 : 400
                                                        }}
                                                      >
                                                        {option}
                                                      </Typography>
                                                    </Box>
                                                  </MenuItem>
                                                )
                                              })}
                                            </Select>
                                          </FormControl>
                                        </Box>
                                      )}
                                    </Box>
                                  </Grid>
                                )}
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>
                      )
                    })}
                  </Grid>
                )}

                {/* No Active Slots Message */}
                {!loadingSlots && slots.length > 0 && !slots.some((slot) => isSlotActive(slot)) && (
                  <Alert
                    severity="info"
                    sx={{
                      mt: 2,
                      borderRadius: 2,
                      background: "linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(3, 169, 244, 0.1) 100%)",
                      border: "1px solid rgba(33, 150, 243, 0.3)",
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      No active slots at the moment
                    </Typography>
                    <Typography variant="body2">
                      There are no active slots right now. Please check back during the scheduled slot times.
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )

      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
          >
            <Card
              sx={{
                background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255,255,255,0.3)",
                borderRadius: 3,
                boxShadow: "0 15px 50px rgba(0,0,0,0.08)",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 }, mb: { xs: 1.5, sm: 2 } }}>
                    <DescriptionIcon
                      sx={{
                        fontSize: { xs: 20, sm: 24, md: 28 },
                        background: "linear-gradient(45deg, #20d5d5 30%, #16B1C8 90%)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    />
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{
                      fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
                      background: "linear-gradient(45deg, #20d5d5 30%, #16B1C8 90%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Request Details
                  </Typography>
                </Box>

                <TextField
                  required
                  fullWidth
                  label="Reason for Request"
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  multiline
                  rows={{ xs: 2, sm: 3 }}
                  inputProps={{ maxLength: 200 }}
                  helperText={`${formData.reason.length}/200 characters`}
                  sx={{
                    mb: { xs: 1, sm: 2 },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      background: "linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(248,250,252,0.8) 100%)",
                      backdropFilter: "blur(10px)",
                      "&:hover fieldset": {
                        borderColor: "#20d5d5",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#16B1C8",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#16B1C8",
                    },
                  }}
                />
              </CardContent>
            </Card>
          </motion.div>
        )

      case 2:
        const selectedSlot = getSelectedSlot()

        return (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
          >
            <Card
              sx={{
                background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255,255,255,0.3)",
                borderRadius: 3,
                boxShadow: "0 15px 50px rgba(0,0,0,0.08)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "linear-gradient(135deg, rgba(32, 213, 213, 0.1) 0%, rgba(22, 177, 200, 0.1) 100%)",
                  pointerEvents: "none",
                },
              }}
            >
              <CardContent sx={{ p: 3, position: "relative", zIndex: 1 }}>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  gutterBottom
                  sx={{
                    background: "linear-gradient(45deg, #20d5d5 30%, #16B1C8 90%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    mb: 2,
                  }}
                >
                  Review Your Request
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        background: "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.9) 100%)",
                        borderRadius: 2,
                        mb: 1.5,
                        border: "1px solid rgba(32, 213, 213, 0.2)",
                        boxShadow: "0 4px 15px rgba(32, 213, 213, 0.1)",
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#16B1C8", mb: 0.5 }}>
                        Selected Slot
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {selectedSlot?.name || "No slot selected"}
                      </Typography>
                      {selectedSlot && (
                        <Typography variant="body2" color="text.secondary">
                          {selectedSlot.startTime} - {selectedSlot.endTime}
                        </Typography>
                      )}
                    </Box>

                    <Box
                      sx={{
                        p: 2,
                        background: "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.9) 100%)",
                        borderRadius: 2,
                        border: "1px solid rgba(32, 213, 213, 0.2)",
                        boxShadow: "0 4px 15px rgba(32, 213, 213, 0.1)",
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#16B1C8", mb: 0.5 }}>
                        Selected Option
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {formData.selectedItemOption || "No option selected"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        background: "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.9) 100%)",
                        borderRadius: 2,
                        border: "1px solid rgba(32, 213, 213, 0.2)",
                        boxShadow: "0 4px 15px rgba(32, 213, 213, 0.1)",
                        height: "100%",
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#16B1C8", mb: 0.5 }}>
                        Reason
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formData.reason || "No reason provided"}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </motion.div>
        )

      default:
        return "Unknown step"
    }
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 1, sm: 2 }, px: { xs: 1, sm: 2, md: 3 }, pb: { xs: 10, sm: 2 } }}>
      <motion.div variants={containerVariants} initial="hidden" animate="visible">
        {/* Mobile-Optimized Header */}
        <motion.div variants={itemVariants}>
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              mb: { xs: 1, sm: 2 },
              p: { xs: 1, sm: 2, md: 3 },
              background: "linear-gradient(135deg, #2c3e50 0%, #34495e 50%, #3d566e 100%)",
              borderRadius: { xs: 2, sm: 3 },
              color: "white",
              textAlign: "center",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "radial-gradient(circle at 80% 20%, rgba(255,255,255,0.15) 0%, transparent 50%)",
                pointerEvents: "none",
              },
            }}
          >
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <RestaurantIcon sx={{ fontSize: { xs: 24, sm: 28, md: 32 }, mb: { xs: 0.5, sm: 1 } }} />
              <Typography
                variant="h4"
                fontWeight={800}
                sx={{
                  fontSize: { xs: "1.25rem", sm: "1.75rem", md: "2.125rem" },
                  background: "linear-gradient(45deg, #fff 30%, #ffe0b2 90%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mb: { xs: 0.25, sm: 0.5 },
                  lineHeight: 1.2,
                }}
              >
                {id ? "Edit Request" : "New Request"}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  opacity: 0.9,
                  fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
                  display: { xs: "none", sm: "block" }
                }}
              >
                Select a slot and submit your food request
              </Typography>
            </Box>
          </Box>
        </motion.div>

        {/* Mobile-Optimized Stepper */}
        <motion.div variants={itemVariants}>
          <Paper
            sx={{
              p: { xs: 1, sm: 1.5, md: 2 },
              mb: { xs: 1, sm: 2 },
              borderRadius: { xs: 2, sm: 3 },
              background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255,255,255,0.3)",
              boxShadow: "0 15px 50px rgba(0,0,0,0.08)",
            }}
          >
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{
                "& .MuiStepLabel-label": {
                  fontWeight: 600,
                  fontSize: { xs: "12px", sm: "14px" },
                },
                "& .MuiStepLabel-label.Mui-active": {
                  color: "#16B1C8",
                },
                "& .MuiStepIcon-root": {
                  fontSize: { xs: "1.2rem", sm: "1.5rem" },
                },
                "& .MuiStepIcon-root.Mui-active": {
                  color: "#20d5d5",
                },
                "& .MuiStepIcon-root.Mui-completed": {
                  color: "#2e7d32",
                },
                "& .MuiStepConnector-line": {
                  borderTopWidth: { xs: 1, sm: 2 },
                },
              }}
            >
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>
        </motion.div>

        {/* Mobile-Optimized Form Content */}
        <motion.div variants={itemVariants}>
          <Paper
            sx={{
              p: { xs: 1.5, sm: 2, md: 3 },
              borderRadius: { xs: 2, sm: 3 },
              mb: { xs: 1, sm: 2 },
              background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255,255,255,0.3)",
              boxShadow: "0 15px 50px rgba(0,0,0,0.08)",
              position: "relative",
            }}
          >
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(8px)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1000,
                  borderRadius: 3,
                }}
              >
                <LoadingSpinner message="Submitting your request..." />
              </Box>
            )}

            <AnimatePresence mode="wait">{getStepContent(activeStep)}</AnimatePresence>

            {/* Mobile-Optimized Navigation Buttons */}
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: { xs: 2, sm: 3 }, gap: { xs: 1, sm: 2 } }}>
              <motion.div whileTap={{ scale: 0.95 }}>
                <Button
                  disabled={activeStep === 0 || loading}
                  onClick={handleBack}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    fontWeight: 600,
                    px: { xs: 2, sm: 3 },
                    py: { xs: 0.75, sm: 1 },
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    border: "2px solid #2c3e50",
                    color: "#2c3e50",
                    background: "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.9) 100%)",
                    backdropFilter: "blur(10px)",
                    "&:hover": {
                      border: "2px solid #34495e",
                      background: "linear-gradient(135deg, #2c3e50 0%, #34495e 100%)",
                      color: "white",
                      boxShadow: "0 8px 25px rgba(44, 62, 80, 0.3)",
                    },
                    "&:disabled": {
                      border: "2px solid #e0e0e0",
                      color: "#bdbdbd",
                      background: "linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%)",
                    },
                  }}
                >
                  ← Back
                </Button>
              </motion.div>

              <motion.div whileTap={{ scale: 0.95 }}>
                {activeStep === steps.length - 1 ? (
                  <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!formData.reason || !formData.selectedSlotId || !formData.selectedItemOption || loading}
                    sx={{
                      borderRadius: { xs: 2, sm: 3 },
                      fontWeight: 700,
                      px: { xs: 3, sm: 4, md: 6 },
                      py: { xs: 1, sm: 1.25, md: 1.5 },
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                      background: "linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)",
                      boxShadow: "0 8px 25px rgba(46, 125, 50, 0.4)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)",
                        boxShadow: "0 12px 35px rgba(46, 125, 50, 0.5)",
                      },
                      "&:disabled": {
                        background: "linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%)",
                        boxShadow: "none",
                      },
                    }}
                  >
                    <SendIcon sx={{ mr: 1, fontSize: 20 }} />
                    {loading ? "Submitting..." : id ? "Update Request" : "Submit Request"}
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    variant="contained"
                    disabled={
                      (activeStep === 0 && (!formData.selectedSlotId || !formData.selectedItemOption)) ||
                      (activeStep === 1 && !formData.reason) ||
                      loading
                    }
                    sx={{
                      borderRadius: { xs: 2, sm: 3 },
                      fontWeight: 700,
                      px: { xs: 3, sm: 4 },
                      py: { xs: 1, sm: 1.25, md: 1.5 },
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                      background: "linear-gradient(135deg, #20d5d5 0%, #16B1C8 100%)",
                      boxShadow: "0 8px 25px rgba(32, 213, 213, 0.4)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
                        boxShadow: "0 12px 35px rgba(32, 213, 213, 0.5)",
                      },
                      "&:disabled": {
                        background: "linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%)",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Next →
                  </Button>
                )}
              </motion.div>
            </Box>
          </Paper>
        </motion.div>

        {/* Mobile-Optimized Progress Indicator */}
        <motion.div variants={itemVariants}>
          <Box
            sx={{
              position: "fixed",
              bottom: { xs: 16, sm: 24 },
              right: { xs: 16, sm: 24 },
              background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)",
              backdropFilter: "blur(20px)",
              borderRadius: { xs: 3, sm: 4 },
              p: { xs: 1.5, sm: 2 },
              border: "1px solid rgba(255,255,255,0.3)",
              boxShadow: "0 25px 80px rgba(0,0,0,0.12)",
              zIndex: 1000,
              display: { xs: "none", sm: "block" },
            }}
          >
            <Typography variant="body2" fontWeight={700} color="text.secondary" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
              Step {activeStep + 1} of {steps.length}
            </Typography>
            <Box
              sx={{
                width: { xs: 80, sm: 100 },
                height: { xs: 4, sm: 6 },
                background: "linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%)",
                borderRadius: 3,
                mt: 1,
                overflow: "hidden",
              }}
            >
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                style={{
                  height: "100%",
                  background: "linear-gradient(135deg, #20d5d5 0%, #16B1C8 100%)",
                  borderRadius: "3px",
                }}
              />
            </Box>
          </Box>
        </motion.div>
      </motion.div>

      {/* Global Styles for Animations */}
      <style jsx>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
      `}</style>
    </Container>
  )
}

export default RequestForm
